import { associationIdName, callApi, fetchGet, fetchPost, siteNumber } from "./common";
import { stripHtml } from "../lib/stripHtml";

export interface PetitionAlert {
    alert: string;
    shortenedAlert: string;
}

export interface Petition {
    id: number;
    title: string;
    officialText: string;
    description?: string;
    url: string;
    imageUrl?: string;
    spotlighted: boolean;
    petitionAlert: PetitionAlert;
    categoryDisplay?: string;
    startDate?: string;
    endDate?: string;
    createdDate: string;
    signatureCount?: number;
    signatureGoal?: number | null;
    confirmationMessage?: string | null;
    sharingEnabled?: boolean;
}

export interface PetitionsResponse {
    spotlightPetitions: Petition[];
    otherPetitions: Petition[];
}

/**
 * Response from GET /api/petitions/{petitionId}
 */
export interface PetitionDetailResponse {
    id: number;
    title: string;
    body: string;
    text: string;
    imageUrl: string | null;
    priority: number;
    timeFrame: {
        start: string;
        end: string;
    };
    confirmationMessage: string | null;
    sharingEnabled: boolean;
    signatureCount?: number;
    signatureGoal?: number | null;
    hideAlert: boolean;
}

export interface PetitionSignatureStatus {
    hasSigned: boolean;
    dateSigned: string;
}

interface PetitionSignatureStatusApiResponse {
    hasSigned?: boolean;
    dateSigned?: string;
    HasSigned?: boolean;
    DateSigned?: string;
}

export interface PetitionShareUrls {
    webShareUrl: string;
    facebookShareUrl: string;
    twitterShareUrl: string;
    linkedInShareUrl: string;
}

export interface SignPetitionRequest {
    userToken: string;
    userId: number;
    fingerprint?: string;
    broadcastReceipt?: string;
    visitIdentifier?: string;
}

export type SocialShareMedium = 'Facebook' | 'Twitter' | 'LinkedIn';
export interface TrackPetitionSocialShareOptions {
    referringNugget?: string;
    userUrlToken?: string;
    fingerprint?: string;
    message?: string;
}

/**
 * Response from GET /api/petitions (Search endpoint)
 */
export interface PetitionApiResponse {
    id: number;
    title: string;
    body: string;
    text: string;
    imageUrl: string | null;
    priority: number;
    timeFrame: {
        start: string;
        end: string;
    };
    confirmationMessage: string | null;
    sharingEnabled: boolean;
    signatureCount?: number;
    signatureGoal?: number | null;
}

// Threshold for determining spotlighted petitions (priority >= this value)
const SPOTLIGHT_PRIORITY_THRESHOLD = 10;

function buildAssociationSiteQuery(): string {
    let query = `association=${associationIdName}`;
    if (siteNumber) {
        query += `&siteNumber=${siteNumber}`;
    }
    return query;
}

function buildPetitionRespondUrl(petitionId: number): string {
    const siteSegment = siteNumber ? `/${siteNumber}` : '';
    return `/${associationIdName}${siteSegment}/Petitions/${petitionId}/Respond`;
}

function mapPetitionResponseToPetition(
    apiPetition: Pick<PetitionDetailResponse, 'id' | 'title' | 'body' | 'text' | 'imageUrl' | 'priority' | 'timeFrame' | 'confirmationMessage' | 'sharingEnabled' | 'signatureCount' | 'signatureGoal' | 'hideAlert'>
): Petition {
    const alertHtml = apiPetition.body || '';
    const alertText = stripHtml(alertHtml);
    return {
        id: apiPetition.id,
        title: stripHtml(apiPetition.title),
        officialText: apiPetition.text || '',
        description: alertHtml,
        url: buildPetitionRespondUrl(apiPetition.id),
        imageUrl: apiPetition.imageUrl || undefined,
        spotlighted: apiPetition.priority >= SPOTLIGHT_PRIORITY_THRESHOLD,
        petitionAlert: { alert: alertHtml, shortenedAlert: alertText },
        startDate: apiPetition.timeFrame.start,
        endDate: apiPetition.timeFrame.end,
        createdDate: apiPetition.timeFrame.start,
        signatureCount: apiPetition.signatureCount ?? 0,
        signatureGoal: apiPetition.signatureGoal ?? null,
        confirmationMessage: apiPetition.confirmationMessage,
        sharingEnabled: apiPetition.sharingEnabled,
        hideAlert: apiPetition.hideAlert,
    };
}

/**
 * Check if a specific user has already signed a petition.
 * Uses a narrow status endpoint instead of the private signatures-read API.
 */
export const getUserPetitionSignatureStatus = async (petitionId: number, userToken: string): Promise<PetitionSignatureStatus | null> => {
    if (!userToken) return null;
    
    try {
        const url = `petitions/${petitionId}/signaturestatus?${buildAssociationSiteQuery()}&userToken=${encodeURIComponent(userToken)}`;
        const response = await fetchGet<PetitionSignatureStatusApiResponse>(url);
        return {
            hasSigned: response.hasSigned ?? response.HasSigned ?? false,
            dateSigned: response.dateSigned ?? response.DateSigned ?? ''
        };
    } catch {
        return null;
    }
};

/** User URL token from server-injected vvSettings (remember-me / identified session). */
export function getPetitionUserUrlToken(): string | undefined {
    const token = (window as any).vvSettings?.userContainer?.userModel?.userToken;
    return typeof token === 'string' && token.trim().length > 0 ? token.trim() : undefined;
}

/** Parallel signature checks for list/related cards; no token returns an empty set. */
export async function getSignedPetitionIdsForUser(
    userToken: string | undefined | null,
    petitionIds: number[]
): Promise<Set<number>> {
    if (!userToken || petitionIds.length === 0) return new Set();
    const uniqueIds = [...new Set(petitionIds)];
    const statuses = await Promise.all(
        uniqueIds.map(async (petitionId) => {
            const status = await getUserPetitionSignatureStatus(petitionId, userToken);
            return status?.hasSigned ? petitionId : null;
        })
    );
    return new Set(statuses.filter((id): id is number => id !== null));
}

export const getPetitionShareUrls = async (
    petitionId: number,
    userToken?: string,
    referringNugget?: string
): Promise<PetitionShareUrls | null> => {
    try {
        const tokenPath = userToken ? `/${encodeURIComponent(userToken)}` : '';
        const vvsnQuery = referringNugget ? `&vvsn=${encodeURIComponent(referringNugget)}` : '';
        const url = `petitions/${petitionId}/shareurls${tokenPath}?${buildAssociationSiteQuery()}${vvsnQuery}`;
        return await fetchGet<PetitionShareUrls>(url);
    } catch {
        return null;
    }
};

/**
 * Get all active petitions for the current association
 * GET /api/petitions?association={association}
 */
export const getPetitions = async (): Promise<PetitionsResponse> => {
    const url = `petitions?${buildAssociationSiteQuery()}`;
    const response = await fetchGet<PetitionApiResponse[]>(url);

    // Convert API response to internal format (strip HTML from text fields)
    const allPetitions: Petition[] = response.map(mapPetitionResponseToPetition);
    
    // Helper to calculate days left from end date
    const getDaysLeft = (endDate?: string): number => {
        if (!endDate) return Infinity; // No end date = lowest urgency
        const end = new Date(endDate);
        const now = new Date();
        return Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    };
    
    // Sort spotlighted petitions: by priority (highest first), then by days left (fewest first = hero)
    const spotlighted = allPetitions.filter(p => p.spotlighted);
    const others = allPetitions.filter(p => !p.spotlighted);
    
    spotlighted.sort((a, b) => {
        const priorityA = response.find(p => p.id === a.id)?.priority || 0;
        const priorityB = response.find(p => p.id === b.id)?.priority || 0;
        
        // First compare by priority (highest first)
        if (priorityB !== priorityA) {
            return priorityB - priorityA;
        }
        
        // Same priority: sort by days left (fewest first = more urgent = hero)
        return getDaysLeft(a.endDate) - getDaysLeft(b.endDate);
    });
    
    // Sort other petitions by days left (most urgent first)
    others.sort((a, b) => getDaysLeft(a.endDate) - getDaysLeft(b.endDate));
    
    return {
        spotlightPetitions: spotlighted,
        otherPetitions: others
    };
};

/**
 * Get a specific petition by ID (basic detail response)
 * GET /api/petitions/{petitionId}?association={association}
 */
export const getPetitionDetail = async (petitionId: number): Promise<PetitionDetailResponse> => {
    const url = `petitions/${petitionId}?${buildAssociationSiteQuery()}`;
    const response = await fetchGet<PetitionDetailResponse>(url);
    return response;
};

async function getPetitionByIdFromList(petitionId: number): Promise<Petition | null> {
    const url = `petitions?${buildAssociationSiteQuery()}`;
    const allPetitions = await fetchGet<PetitionApiResponse[]>(url);
    const apiPetition = allPetitions.find(p => p.id === petitionId);
    if (!apiPetition) {
        return null;
    }

    return mapPetitionResponseToPetition(apiPetition);
}

/**
 * Get a specific petition by ID with full data.
 * Uses the single-petition API endpoint.
 */
export const getPetitionById = async (petitionId: number): Promise<Petition | null> => {
    try {
        const apiPetition = await getPetitionDetail(petitionId);
        return mapPetitionResponseToPetition(apiPetition);
    } catch (error) {
        console.warn('Falling back to petitions list lookup:', error);
        try {
            return await getPetitionByIdFromList(petitionId);
        } catch (fallbackError) {
            console.error('Error fetching petition:', fallbackError);
            return null;
        }
    }
};

/**
 * Sign a petition
 * POST /api/petitions/{petitionId}/signatures?association={association}
 */
export const signPetition = async (
    petitionId: number, 
    request: SignPetitionRequest
): Promise<void> => {
    const url = `petitions/${petitionId}/signatures?${buildAssociationSiteQuery()}`;
    await fetchPost<SignPetitionRequest, void>(url, request);
};

/**
 * Track social share action (parity with legacy SocialMedia/Shared endpoint).
 */
export const trackPetitionSocialShare = async (
    petitionId: number,
    medium: SocialShareMedium,
    promoteUrl: string,
    options?: TrackPetitionSocialShareOptions
): Promise<void> => {
    const params = new URLSearchParams({
        association: associationIdName,
        applicationType: 'Petition',
        actionId: String(petitionId),
        promoteUrl,
        message: options?.message ?? '',
        medium,
        referringNugget: options?.referringNugget ?? ''
    });

    if (options?.userUrlToken) {
        params.set('userUrlToken', options.userUrlToken);
    }
    if (options?.fingerprint) {
        params.set('fingerprint', options.fingerprint);
    }

    const url = `SocialMedia/Shared?${params.toString()}`;
    await callApi<undefined, string>(url, "POST", undefined, true);
};

